import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Box, CircularProgress } from "@mui/material";

import DashboardContainer from "../../components/dashboard-container";
import InventoryConfigModal from "../../components/inventory-config-management";
import InventoryConfigList from "../../components/inventory-config-list";
import CustomPagination from "../../components/pagination";
import { inventoryConfigService } from "../../utils/services/inventory-config.service";
import { productService } from "../../utils/services/productService";
import { toast } from "react-toastify";

const InventoryConfig = () => {
  const { state } = useLocation();
  // `state.product` may be either a full product object or just a product id string
  const lockedProduct = state?.product || null;

  // Resolved full product object (fetched if only id was provided)
  const [resolvedLockedProduct, setResolvedLockedProduct] = useState(
    typeof lockedProduct === "object" && lockedProduct ? lockedProduct : null,
  );

  const [selectedConfig, setSelectedConfig] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [inventoryConfigList, setInventoryConfigList] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [searchValue, setSearchValue] = useState("");
  const [clientFilter, setClientFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dataCount, setDataCount] = useState(rowsPerPage);
  const [loading, setLoading] = useState(false);

  const fetchInventoryConfig = async () => {
    setLoading(true);
    try {
      // Determine product id for fetching configs
      const productIdForFetch =
        resolvedLockedProduct?._id ||
        (typeof lockedProduct === "string" ? lockedProduct : null);

      if (!productIdForFetch) {
        return;
      }

      const res = await inventoryConfigService.fetch(
        rowsPerPage,
        rowsPerPage * page,
        clientFilter?._id,
        searchValue,
        productIdForFetch,
      );

      setInventoryConfigList(res?.data || []);
      setDataCount(res?.total);
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryConfig();
  }, [
    page,
    rowsPerPage,
    clientFilter,
    searchValue,
    resolvedLockedProduct,
    lockedProduct,
  ]);

  // If `lockedProduct` is an id (string) or object without _id, fetch full product
  useEffect(() => {
    let mounted = true;
    const resolveProduct = async () => {
      if (!lockedProduct) {
        setResolvedLockedProduct(null);
        return;
      }

      // If lockedProduct is a string ID, fetch by id
      if (typeof lockedProduct === "string") {
        try {
          const product = await productService.fetchById(lockedProduct);
          if (mounted) setResolvedLockedProduct(product);
        } catch (err) {
          console.error("Failed to fetch locked product by id:", err);
          toast.error("Failed to load product for inventory configuration");
          if (mounted) setResolvedLockedProduct(null);
        }
        return;
      }

      // If it's an object, check if it contains only _id (e.g. { _id: '...' })
      if (typeof lockedProduct === "object") {
        const keys = Object.keys(lockedProduct || {});
        // If only _id present, fetch full product
        if (lockedProduct._id && keys.length === 1) {
          try {
            const product = await productService.fetchById(lockedProduct._id);
            if (mounted) setResolvedLockedProduct(product);
          } catch (err) {
            console.error("Failed to fetch locked product by object _id:", err);
            toast.error("Failed to load product for inventory configuration");
            if (mounted) setResolvedLockedProduct(null);
          }
          return;
        }

        // If object already has more than _id (full object), use it
        if (lockedProduct._id) {
          setResolvedLockedProduct(lockedProduct);
          return;
        }

        setResolvedLockedProduct(null);
        return;
      }
    };

    resolveProduct();

    return () => {
      mounted = false;
    };
  }, [lockedProduct]);

  const showSearchAndClient = !lockedProduct;
  const showPagination = !lockedProduct;

  return (
    <>
      <DashboardContainer
        hideUploadButton
        header="Inventory/Supplier Configuration List"
        title="+ Add"
        onClickAddButton={() => setOpenModal(true)}
        setViewMode={setViewMode}
        onClickRefresh={fetchInventoryConfig}
        hideFilterClient
        filterButton={false}
      >
        <InventoryConfigModal
          open={openModal}
          handleClose={() => {
            setOpenModal(false);
            setSelectedConfig(null);
          }}
          onAddData={fetchInventoryConfig}
          selectedInventoryConfig={selectedConfig}
          lockedProduct={
            resolvedLockedProduct ||
            (typeof lockedProduct === "object" ? lockedProduct : null)
          }
        />

        {loading ? (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <InventoryConfigList
            data={inventoryConfigList}
            viewMode={viewMode}
            dataCount={dataCount}
            page={page}
            rowsPerPage={rowsPerPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            onDeleteData={fetchInventoryConfig}
            onEditData={(cfg) => {
              setSelectedConfig(cfg);
              setOpenModal(true);
            }}
            selectedConfig={selectedConfig}
            setSelectedConfig={setSelectedConfig}
            lockedProduct={
              resolvedLockedProduct ||
              (typeof lockedProduct === "object" ? lockedProduct : null)
            }
          />
        )}

        {showPagination && (
          <CustomPagination
            dataCount={dataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            disableNext={dataCount <= (page + 1) * rowsPerPage}
          />
        )}
      </DashboardContainer>
    </>
  );
};

export default InventoryConfig;
